import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { Loader2, RefreshCw, Gauge, AlertTriangle, CheckCircle } from "lucide-react";

const pctUsed = (used, limit) => limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
const barColor = (pct) => {
  if (pct >= 90) return "bg-red-500";
  if (pct >= 70) return "bg-amber-500";
  return "bg-emerald-500";
};
const apiLabels = {
  census: "Census Bureau",
  yelp: "Yelp Fusion",
  foursquare: "Foursquare",
  nominatim: "Nominatim (OSM)",
  usps: "USPS Address",
  google_places: "Google Places",
  wikipedia: "Wikipedia/Wikidata",
};

export default function RateLimitPanel() {
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLimits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/geo/rate-limits`);
      setApis(res.data.apis || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLimits(); }, [fetchLimits]);

  if (loading && apis.length === 0) return <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>;

  const totalUsed = apis.reduce((s, a) => s + (a.calls_used || 0), 0);
  const activeApis = apis.filter(a => a.daily_limit > 0);
  const pausedApis = apis.filter(a => a.paused || (a.daily_limit > 0 && a.calls_used >= a.daily_limit));

  return (
    <div className="space-y-4" data-testid="rate-limit-panel">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-1">Total Calls Today</div>
          <div className="text-xl font-bold text-slate-800">{totalUsed.toLocaleString()}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-1">Active APIs</div>
          <div className="text-xl font-bold text-emerald-600">{activeApis.length}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-1">At Limit</div>
          <div className="text-xl font-bold text-red-500">{pausedApis.length}</div>
        </div>
      </div>

      <button onClick={fetchLimits} className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 flex items-center gap-1">
        <RefreshCw className="w-3 h-3" /> Refresh
      </button>

      {/* API Usage Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 text-xs font-medium text-slate-500 grid grid-cols-[1fr,90px,90px,120px,60px] gap-2">
          <span>API Service</span>
          <span className="text-right">Used</span>
          <span className="text-right">Limit</span>
          <span>Usage</span>
          <span className="text-right">Status</span>
        </div>
        <div className="divide-y divide-slate-100">
          {apis.map((api) => {
            const pct = pctUsed(api.calls_used, api.daily_limit);
            const atLimit = api.daily_limit > 0 && api.calls_used >= api.daily_limit;
            const dormant = api.daily_limit === 0;
            return (
              <div key={api.api_name} className={`px-4 py-2.5 grid grid-cols-[1fr,90px,90px,120px,60px] gap-2 items-center ${dormant ? "opacity-50" : ""}`} data-testid={`rate-limit-${api.api_name}`}>
                <div className="flex items-center gap-2">
                  <Gauge className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">
                    {apiLabels[api.api_name] || api.api_name}
                  </span>
                </div>
                <span className="text-xs text-slate-600 text-right font-mono">
                  {(api.calls_used || 0).toLocaleString()}
                </span>
                <span className="text-xs text-slate-400 text-right font-mono">
                  {dormant ? "—" : (api.daily_limit || 0).toLocaleString()}
                </span>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${barColor(pct)}`} style={{ width: `${dormant ? 0 : pct}%` }} />
                  </div>
                  <span className="text-[10px] text-slate-400 w-8 text-right">{dormant ? "—" : `${pct}%`}</span>
                </div>
                <div className="flex justify-end">
                  {dormant ? (
                    <span className="text-[10px] text-slate-400">Off</span>
                  ) : atLimit ? (
                    <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                  ) : (
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-[10px] text-slate-400">
        Rate limits reset daily at midnight UTC. APIs that hit their limit will automatically resume the next day.
      </p>
    </div>
  );
}
